import {currencies, formatCurrencySymbol} from 'platform/locale';
import {match} from 'ts-pattern';

import {isNil} from 'ramda';

import {CurrencyCodeType} from 'shared/web';

import i18n from '../../../../../i18n/i18n';
import {roundByCurrency} from '../../../../../utils/roundByCurrency';

type FormatChartPriceLabelDeps = {
  formatNumber: (value: number, decimals?: number) => string | null;
};

function formatThousandsTick(
  thousands: number,
  formatNumber: FormatChartPriceLabelDeps['formatNumber']
): string {
  const roundedToTenth = Math.round(thousands * 10) / 10;

  if (roundedToTenth % 1 === 0) {
    return formatNumber(roundedToTenth) ?? '';
  }

  return formatNumber(roundedToTenth, 1) ?? '';
}

export function formatChartPriceThousandsTick(
  value: number,
  currencyCode: CurrencyCodeType | undefined,
  {formatNumber}: FormatChartPriceLabelDeps,
  suffix = ''
): string {
  if (isNil(currencyCode)) {
    return `${formatNumber(Math.round(value / 1000)) ?? ''}${suffix}`;
  }

  const roundedValue = roundByCurrency(value, currencyCode);
  const thousands = roundedValue / 1000;
  const formattedTick =
    currencyCode === 'CZK'
      ? (formatNumber(Math.round(thousands)) ?? '')
      : formatThousandsTick(thousands, formatNumber);

  return `${formattedTick}${suffix}`;
}

export function formatChartPriceLabel(
  value: number,
  currencyCode: CurrencyCodeType,
  {formatNumber}: FormatChartPriceLabelDeps
): string {
  const roundedValue = roundByCurrency(value, currencyCode);
  const currencySymbol = formatCurrencySymbol(currencies, currencyCode);

  return match(currencyCode)
    .with('CZK', () =>
      i18n.t('entity.statisticsChart.labels.thousandsCurrency', {
        value: formatNumber(Math.round(roundedValue / 1000)) ?? '',
        currencySymbol,
      })
    )
    .otherwise(() => `${formatNumber(roundedValue) ?? ''} ${currencySymbol}`);
}
